import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { apiGet } from '../../DataService/apiClient';
import { AdminInvoice, AdminOrder, AdminOrdersResponse, AdminProviderRenewal, AdminProviderSignalSnapshot, AdminProviderSyncRun } from '../../Types/AdminOrderTypes';
import { PaymentService } from '../../DataService/PaymentService';
import { Button } from '../../Components/Button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../Components/Tabs';
import { AdminIcon } from '../../Icons/AdminIcon';
import { AdminInvoiceForm } from './AdminInvoiceForm';
import './AdminHome.css';

type MetricTone = 'slate' | 'emerald' | 'amber' | 'rose' | 'blue';

type MetricIcon = React.ComponentType<{ width?: number; height?: number; fill?: string }>;

const currencyFormatter = new Intl.NumberFormat('sv-SE', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});

const formatMoney = (value: number) => `${currencyFormatter.format(value)} SEK`;

const getOrderStatusText = (status: string) => {
  switch (status.toLowerCase()) {
    case 'pending': return 'Väntande';
    case 'paid': return 'Betald';
    case 'failed': return 'Misslyckad';
    case 'fulfilled': return 'Slutförd';
    case 'cancelled': return 'Avbruten';
    default: return status;
  }
};

const getOrderStatusTone = (status: string): MetricTone => {
  switch (status.toLowerCase()) {
    case 'paid':
    case 'fulfilled':
      return 'emerald';
    case 'pending':
      return 'amber';
    case 'failed':
    case 'cancelled':
      return 'rose';
    default:
      return 'slate';
  }
};

const getToneStyles = (tone: MetricTone): React.CSSProperties => {
  switch (tone) {
    case 'emerald': return { background: 'hsl(var(--secondary) / 0.12)', color: 'hsl(var(--secondary))', borderColor: 'hsl(var(--secondary) / 0.18)' };
    case 'amber': return { background: 'hsl(var(--accent) / 0.12)', color: 'hsl(var(--accent))', borderColor: 'hsl(var(--accent) / 0.18)' };
    case 'rose': return { background: 'hsl(var(--destructive) / 0.10)', color: 'hsl(var(--destructive))', borderColor: 'hsl(var(--destructive) / 0.18)' };
    case 'blue': return { background: 'hsl(var(--primary) / 0.12)', color: 'hsl(var(--primary))', borderColor: 'hsl(var(--primary) / 0.18)' };
    default: return { background: 'hsl(var(--muted) / 0.85)', color: 'hsl(var(--foreground))', borderColor: 'hsl(var(--border))' };
  }
};

const StatusMetric = ({ label, value, sub, icon: Icon, tone }: { label: string; value: string; sub: string; icon: MetricIcon; tone: MetricTone }) => (
  <article className="admin-metric-card admin-surface-card">
    <div className="admin-metric-card__icon" style={getToneStyles(tone)}>
      <Icon width={18} height={18} fill="currentColor" />
    </div>
    <div className="admin-metric-card__body">
      <span>{label}</span>
      <strong>{value}</strong>
      <small>{sub}</small>
    </div>
  </article>
);

const StatusDot = ({ status }: { status: string }) => (
  <span className={`admin-status-dot admin-status-dot--${getOrderStatusTone(status)}`} aria-hidden="true" />
);

const DetailBox = ({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) => (
  <div className="admin-order-detail-box">
    <span>{label}</span>
    <strong className={mono ? 'admin-order-detail-box--mono' : ''}>{value}</strong>
  </div>
);

const TimelineDate = ({ label, date }: { label: string; date?: string | null }) => (
  <div className="admin-order-timeline-item">
    <span>{label}</span>
    <strong>{date ? new Date(date).toLocaleString() : '-'}</strong>
  </div>
);

const PaymentLogRow = ({
  title,
  subtitle,
  amount,
  amountLabel,
  meta,
  highlight
}: {
  title: string;
  subtitle: string;
  amount?: string;
  amountLabel?: string;
  meta?: string;
  highlight?: string;
}) => (
  <div className="admin-order-log-row">
    <div>
      <strong>{title}</strong>
      <p>{subtitle}</p>
      {highlight && <p className="admin-order-log-row-highlight">{highlight}</p>}
    </div>
    <div className="admin-order-log-row-meta">
      {amount && <strong>{amount}</strong>}
      {amountLabel && <span>{amountLabel}</span>}
      {meta && <span>{meta}</span>}
    </div>
  </div>
);

const getProviderStatusTone = (status: string): MetricTone => {
  switch (status.toLowerCase()) {
    case 'healthy':
    case 'succeeded':
    case 'info':
      return 'emerald';
    case 'warning':
    case 'running':
      return 'amber';
    case 'critical':
    case 'failed':
      return 'rose';
    default:
      return 'slate';
  }
};

const formatProviderSignalValue = (signal: AdminProviderSignalSnapshot) => {
  if (signal.signalType === 'flag') {
    return signal.valueNumber && signal.valueNumber > 0 ? 'Ja' : 'Nej';
  }

  if (signal.valueText && signal.valueText.trim().length > 0) {
    return signal.valueText;
  }

  if (signal.valueNumber !== null && signal.valueNumber !== undefined) {
    return signal.currency ? `${currencyFormatter.format(signal.valueNumber)} ${signal.currency}` : String(signal.valueNumber);
  }

  return '-';
};

const formatProviderSignalMeta = (signal: AdminProviderSignalSnapshot) => {
  if (!signal.metadataJson) return null;

  try {
    const metadata = JSON.parse(signal.metadataJson) as {
      source?: string;
      actualPaymentCount?: number;
      estimatedPaymentCount?: number;
      successfulPaymentCount?: number;
    };

    const parts: string[] = [];

    if (metadata.source) {
      if (metadata.source === 'Stripe balance transactions') {
        parts.push('Faktisk Stripe-fee');
      } else if (metadata.source === 'Configured fee estimate') {
        parts.push('Beräknad Stripe-fee');
      } else {
        parts.push(metadata.source);
      }
    }

    if (metadata.actualPaymentCount !== undefined || metadata.estimatedPaymentCount !== undefined) {
      parts.push(`Faktiska ${metadata.actualPaymentCount ?? 0}, est ${metadata.estimatedPaymentCount ?? 0}`);
    }

    if (metadata.successfulPaymentCount !== undefined) {
      parts.push(`${metadata.successfulPaymentCount} lyckade betalningar`);
    }

    return parts.length > 0 ? parts.join(' · ') : null;
  } catch {
    return null;
  }
};

const formatProviderRenewalValue = (renewal: AdminProviderRenewal) => {
  if (!renewal.dueAt) return 'Ej satt';
  return new Date(renewal.dueAt).toLocaleDateString('sv-SE');
};

export const AdminOrdersPage = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState<AdminOrdersResponse | null>(null);
  const [invoices, setInvoices] = useState<AdminInvoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const getRotPaymentLabel = (stage?: string | null) => {
    if (!stage) return null;
    if (stage === 'registration') return 'ROT: registrerad';
    if (stage === 'balance') return 'ROT: fullt betald';
    return null;
  };

  const loadData = useCallback(async (mode: 'initial' | 'refresh' = 'refresh') => {
    if (mode === 'initial') {
      setLoading(true);
    } else {
      setRefreshing(true);
    }

    try {
      const res = await apiGet<AdminOrdersResponse>('/AdminOrders');
      setData(res);

      try {
        setInvoices(await PaymentService.GetAdminInvoices());
      } catch (invoiceErr) {
        console.error('Admin invoices load error', invoiceErr);
      }
    } catch (err) {
      console.error('Admin orders load error', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    void loadData('initial');
  }, [loadData]);

  const selectedOrder: AdminOrder | null = useMemo(() => {
    if (!data) return null;
    if (!orderId) return data.orders[0] ?? null;
    return data.orders.find(order => order.id === orderId || order.orderNumber === orderId) ?? null;
  }, [data, orderId]);

  const selectedOrderInvoices = useMemo(() => {
    if (!selectedOrder) return [];
    return invoices.filter(invoice => invoice.orderId === selectedOrder.id || invoice.orderNumber === selectedOrder.orderNumber);
  }, [invoices, selectedOrder]);

  const selectedOrderProgramStage = useMemo(() => {
    return selectedOrder?.items.find(item => item.programPaymentStage)?.programPaymentStage ?? null;
  }, [selectedOrder]);

  const latestProviderRun: AdminProviderSyncRun | null = useMemo(() => {
    return data?.providerSyncRuns[0] ?? null;
  }, [data]);

  const providerSignals = useMemo(() => {
    return data?.providerSignalSnapshots.slice(0, 12) ?? [];
  }, [data]);

  const providerRenewals = useMemo(() => {
    return data?.providerRenewals ?? [];
  }, [data]);

  const invoiceActionLabel = selectedOrderProgramStage === 'registration'
    ? 'Markera registrerad'
    : selectedOrderProgramStage === 'balance'
      ? 'Markera fullt betald'
      : 'Markera betald';

  const invoiceActionHint = selectedOrderProgramStage === 'registration'
    ? 'Låser upp prep-modulen och startar ROT-flödet.'
    : selectedOrderProgramStage === 'balance'
      ? 'Slutför ROT-programmet och markera hela betalningen.'
      : 'Markerar fakturan som betald.';

  const markInvoicePaid = async (invoiceId: string) => {
    await PaymentService.MarkInvoicePaid(invoiceId);
    setInvoices(prev => prev.map(invoice => invoice.id === invoiceId ? { ...invoice, status: 'Paid', paidAt: new Date().toISOString() } : invoice));
  };

  if (loading) {
    return <div className="admin-page-shell"><p>Laddar ordrar...</p></div>;
  }

  if (!data) {
    return <div className="admin-page-shell"><p>Kunde inte ladda ordrar.</p></div>;
  }

  const metrics = [
    { label: 'Totala Ordrar', value: String(data.totalOrders), sub: 'st', icon: AdminIcon.Graph, tone: 'slate' as const },
    { label: 'Betalda', value: String(data.paidOrders), sub: 'st', icon: AdminIcon.CheckCircle, tone: 'emerald' as const },
    { label: 'Väntande', value: String(data.pendingOrders), sub: 'st', icon: AdminIcon.Clock, tone: 'amber' as const },
    { label: 'Misslyckade', value: String(data.failedOrders), sub: 'st', icon: AdminIcon.Trashcan, tone: 'rose' as const },
    { label: 'Omsättning', value: new Intl.NumberFormat('sv-SE').format(data.totalRevenue), sub: 'SEK', icon: AdminIcon.Gifts, tone: 'blue' as const },
  ];

  return (
    <div className="admin-page-shell admin-orders-shell">
      <section className="admin-orders-hero admin-surface-card admin-surface-card--soft">
        <div className="admin-orders-hero-copy">
          <div className="admin-orders-hero-kicker">
            <AdminIcon.Graph width={18} height={18} fill="hsl(var(--primary))" />
            <span>Ordrar och betalningar</span>
          </div>
          <h1 className="admin-page-title">Ordrar och betalningar</h1>
          <p className="admin-page-subtitle">Översikt över checkout, Stripe och återkommande betalningar</p>
        </div>
        <div className="admin-orders-hero-actions">
          <Button
            text={refreshing ? 'Uppdaterar...' : 'Uppdatera'}
            variant="secondary"
            size="sm"
            onClick={() => void loadData('refresh')}
            disabled={refreshing}
          />
          <span className="admin-orders-hero-hint">{selectedOrder ? `Markerad: ${selectedOrder.orderNumber}` : 'Välj en order för detaljvy'}</span>
        </div>
      </section>

      <div className="admin-orders-kpi-grid">
        {metrics.map(metric => (
          <StatusMetric key={metric.label} {...metric} />
        ))}
      </div>

      <section className="admin-surface-card admin-card admin-provider-summary-card">
        <div className="admin-order-payments-header">
          <div>
            <h2>Provider sync</h2>
            <p>Normaliserad drift- och kostnadsdata från backend.</p>
          </div>
          {latestProviderRun && (
            <span className={`admin-status-pill admin-status-pill--${getProviderStatusTone(latestProviderRun.status)}`}>
              {latestProviderRun.provider} · {latestProviderRun.syncType} · {latestProviderRun.status}
            </span>
          )}
        </div>

        <div className="admin-provider-summary-grid">
          <div className="admin-provider-summary-run">
            {latestProviderRun ? (
              <>
                <strong>Senaste körning</strong>
                <p>{new Date(latestProviderRun.startedAt).toLocaleString()}</p>
                <p>{latestProviderRun.finishedAt ? `Slutade ${new Date(latestProviderRun.finishedAt).toLocaleString()}` : 'Pågår'}</p>
                <p>{latestProviderRun.itemsProcessed} poster</p>
                {latestProviderRun.errorMessage && <p>{latestProviderRun.errorMessage}</p>}
              </>
            ) : (
              <p>Ingen provider-sync har körts ännu.</p>
            )}
          </div>

          <div className="admin-provider-summary-signals">
            {providerSignals.length > 0 ? providerSignals.map(signal => {
                const signalMeta = formatProviderSignalMeta(signal);

                return (
              <div key={signal.id} className="admin-provider-signal-row">
                <div>
                  <strong>{signal.signalKey}</strong>
                  <p>{signal.signalType} · {signal.provider}</p>
                </div>
                <div className="admin-provider-signal-row__meta">
                  <span className={`admin-status-pill admin-status-pill--${getProviderStatusTone(signal.status)}`}>{signal.status}</span>
                  <strong>{formatProviderSignalValue(signal)}</strong>
                  <span>{new Date(signal.lastSyncedAt).toLocaleString()}</span>
                  {signalMeta && <span>{signalMeta}</span>}
                </div>
              </div>
                );
              }) : <p>Inga provider-signaler ännu.</p>}
          </div>
        </div>

        <div className="admin-provider-renewal-grid">
          {providerRenewals.length > 0 ? providerRenewals.map(renewal => (
            <div key={renewal.id} className="admin-provider-renewal-row">
              <div>
                <strong>{renewal.provider} · {renewal.name}</strong>
                <p>{renewal.owner ?? 'Ägare ej satt'}</p>
                <p>{renewal.notes ?? 'Manual renewal record'}</p>
              </div>
              <div className="admin-provider-renewal-row__meta">
                <span className={`admin-status-pill admin-status-pill--${getProviderStatusTone(renewal.status)}`}>{renewal.status}</span>
                <strong>{formatProviderRenewalValue(renewal)}</strong>
                <span>{renewal.amount ? `${currencyFormatter.format(renewal.amount)} ${renewal.currency ?? ''}`.trim() : 'Ingen avgift satt'}</span>
              </div>
            </div>
          )) : (
            <div className="admin-provider-renewal-row">
              <div>
                <strong>Simply</strong>
                <p>Ingen renewal-post har synkats ännu.</p>
              </div>
            </div>
          )}
        </div>
      </section>

      <div className="admin-orders-bento-grid">
        <section className="admin-surface-card admin-card admin-orders-list-card">
          <div className="admin-orders-flow-header">
            <div>
              <h2>Senaste flödet</h2>
              <p>Välj en order för att se betalningar, status och händelser.</p>
            </div>
            <div className="admin-orders-flow-indicator">
              <span className="admin-orders-flow-indicator-dot" />
              <span>Live</span>
            </div>
          </div>

          <div className="admin-orders-list admin-orders-list--scroll">
            {data.orders.length > 0 ? data.orders.map(order => (
              <button
                key={order.id}
                className={`admin-order-row admin-order-row--flow ${selectedOrder?.id === order.id ? 'active' : ''}`}
                onClick={() => navigate(`/admin/orders/${order.id}`)}
              >
                {selectedOrder?.id === order.id && <span className="admin-order-row-accent" />}
                <div className="admin-order-row-main">
                  <div className="admin-order-row-topline">
                    <span className="admin-order-row-id">{order.orderNumber}</span>
                    <StatusDot status={order.status} />
                  </div>
                  <div className="admin-order-row-body">
                    <div>
                      <strong>{order.customerName}</strong>
                      <p>{order.items.map(item => item.title).join(', ')}</p>
                    </div>
                    <div className="admin-order-row-meta">
                      <strong>{formatMoney(order.totalAmount)}</strong>
                      <span>{getOrderStatusText(order.status)}</span>
                    </div>
                  </div>
                </div>
              </button>
            )) : (
              <div className="admin-order-empty-state">
                <AdminIcon.Graph width={34} height={34} fill="hsl(var(--muted-foreground))" />
                <p>Inga ordrar ännu.</p>
              </div>
            )}
          </div>
        </section>

        <div className="admin-orders-detail-stack">
          {selectedOrder ? (
            <section className="admin-surface-card admin-card admin-orders-detail-card">
              <div className="admin-order-detail-header admin-order-detail-header--hero">
                <div>
                  <div className="admin-order-detail-kicker">
                    <span className="admin-status-pill admin-status-pill--soft">Order</span>
                    <span className="admin-order-detail-id">{selectedOrder.orderNumber}</span>
                  </div>
                  <h2>{selectedOrder.customerName}</h2>
                  <p>{selectedOrder.email}</p>
                </div>
                <div className="admin-order-detail-actions">
                  <span className={`admin-status-pill admin-status-pill--${getOrderStatusTone(selectedOrder.status)}`}>
                    {getOrderStatusText(selectedOrder.status)}
                  </span>
                  <Link
                    className="admin-link-btn"
                    to={`/member/orders/${selectedOrder.id}`}
                    state={{ order: selectedOrder, supportView: true }}
                  >
                    Visa medlemsvy
                  </Link>
                </div>
              </div>

              <div className="admin-order-detail-grid">
                <DetailBox label="Payment ref" value={selectedOrder.paymentReference} mono />
                <DetailBox label="Metod" value={selectedOrder.paymentMethod} />
                <DetailBox label="Totalsumma" value={formatMoney(selectedOrder.totalAmount)} />
                <DetailBox label="Status" value={getOrderStatusText(selectedOrder.status)} />
                <DetailBox label="Skapad" value={new Date(selectedOrder.createdAt).toLocaleString()} />
                <DetailBox label="Fulfilled" value={selectedOrder.fulfilledAt ? new Date(selectedOrder.fulfilledAt).toLocaleString() : '-'} />
              </div>

              <div className="admin-order-timeline-grid">
                <TimelineDate label="Skapad" date={selectedOrder.createdAt} />
                <TimelineDate label="Verifierad" date={selectedOrder.paidAt} />
                <TimelineDate label="Slutförd" date={selectedOrder.fulfilledAt} />
              </div>

              <div className="admin-order-items admin-order-summary-box">
                <div className="admin-order-summary-box__header">
                  <span>Produkt / Beskrivning</span>
                  <span>Summa</span>
                </div>
                {selectedOrder.items.map(item => (
                  <div key={item.id} className="admin-order-item-row admin-order-summary-row">
                    <div>
                      <strong>{item.title}</strong>
                      <p>{item.itemType} · Antal {item.quantity}</p>
                      {getRotPaymentLabel(item.programPaymentStage) && <p>{getRotPaymentLabel(item.programPaymentStage)}</p>}
                      {item.bookingMode && (
                        <p>
                          Bokning: {item.bookingMode === 'intro'
                            ? 'introduktionssamtal'
                            : item.bookingMode === 'included'
                              ? `inkluderad från ${item.includedSessionSource ?? '-'}`
                              : 'betald session'}
                        </p>
                      )}
                    </div>
                    <strong>{formatMoney(item.lineAmount)}</strong>
                  </div>
                ))}

                <div className="admin-order-summary-total">
                  <span>Total debitering</span>
                  <strong>{formatMoney(selectedOrder.totalAmount)}</strong>
                </div>
              </div>
            </section>
          ) : (
            <section className="admin-surface-card admin-card admin-orders-detail-card admin-orders-empty-detail">
              <AdminIcon.Graph width={40} height={40} fill="hsl(var(--muted-foreground))" />
              <p>Inget valt objekt</p>
            </section>
          )}

          <section className="admin-surface-card admin-card admin-order-payments-section">
            <div className="admin-order-payments-header">
              <div>
                <h2>Systemloggar för order</h2>
                <p>Verifikation av externa API-anrop</p>
              </div>
            </div>

            <Tabs defaultValue="stripe" className="admin-order-payments-tabs">
              <TabsList className="admin-order-payments-tabs-list">
                <TabsTrigger value="stripe">Stripe</TabsTrigger>
                <TabsTrigger value="subscriptions">Subscriptions</TabsTrigger>
                <TabsTrigger value="swish">Swish</TabsTrigger>
                <TabsTrigger value="invoices">Fakturor</TabsTrigger>
              </TabsList>

              <TabsContent value="stripe" className="admin-order-payments-tab-content">
                <div className="admin-order-payment-panel admin-order-payment-panel--scroll">
                  {selectedOrder ? selectedOrder.stripePayments.length > 0 ? selectedOrder.stripePayments.map(payment => (
                    <PaymentLogRow
                      key={payment.id}
                      title={payment.paymentIntentId}
                      subtitle={`${payment.status} · ${payment.paymentMethod ?? 'unknown'}`}
                      highlight={payment.metadata?.slice(0, 120) ?? '-'}
                      amount={formatMoney(payment.amount / 100)}
                      amountLabel={payment.currency.toUpperCase()}
                      meta={new Date(payment.createdUtc).toLocaleString()}
                    />
                  )) : <p>Inga Stripe-events kopplade till ordern.</p> : <p>Välj en order för att se Stripe-händelser.</p>}
                </div>
              </TabsContent>

              <TabsContent value="subscriptions" className="admin-order-payments-tab-content">
                <div className="admin-order-payment-panel admin-order-payment-panel--scroll">
                  {data.stripeSubscriptions.length > 0 ? data.stripeSubscriptions.map(subscription => (
                    <PaymentLogRow
                      key={subscription.id}
                      title={subscription.stripeSubscriptionId}
                      subtitle={`${subscription.status} · ${subscription.stripeCustomerId}`}
                      highlight={`Price: ${subscription.priceId}`}
                      meta={`Skapad: ${new Date(subscription.createdUtc).toLocaleString()}${subscription.currentPeriodEnd ? ` · Period slut: ${new Date(subscription.currentPeriodEnd).toLocaleString()}` : ''}`}
                    />
                  )) : <p>Inga subscriptions ännu.</p>}
                </div>
              </TabsContent>

              <TabsContent value="swish" className="admin-order-payments-tab-content">
                <div className="admin-order-payment-panel admin-order-payment-panel--scroll">
                  {selectedOrder ? selectedOrder.swishTransactions.length > 0 ? selectedOrder.swishTransactions.map(tx => (
                    <PaymentLogRow
                      key={tx.id}
                      title={tx.swishId ?? tx.originalPaymentReference ?? 'Swish'}
                      subtitle={`${tx.type} · ${tx.status ?? '-'}`}
                      highlight={tx.message ?? '-' }
                      amount={formatMoney(tx.amount)}
                      amountLabel={tx.currency.toUpperCase()}
                      meta={new Date(tx.createdAt).toLocaleString()}
                    />
                  )) : <p>Inga Swish-events kopplade till ordern.</p> : <p>Välj en order för att se Swish-händelser.</p>}
                </div>
              </TabsContent>

              <TabsContent value="invoices" className="admin-order-payments-tab-content">
                <div className="admin-order-payment-panel admin-order-payment-panel--scroll">
                  <div style={{ marginBottom: 16 }}>
                    <AdminInvoiceForm />
                  </div>
                  {selectedOrderInvoices.length > 0 ? selectedOrderInvoices.map(invoice => (
                    <div key={invoice.id} className="admin-order-payment-row admin-order-payment-row--compact">
                      <div>
                        <strong>{invoice.invoiceNumber}</strong>
                        <p>{invoice.email} · {invoice.paymentMethod}</p>
                        <p>{invoice.status} · Order {invoice.orderNumber ?? '-'}</p>
                        {selectedOrderProgramStage && (
                          <p>{selectedOrderProgramStage === 'registration' ? 'ROT: delbetalning' : 'ROT: slutbetalning'}</p>
                        )}
                      </div>
                      <div className="admin-order-row-meta" style={{ gap: 8 }}>
                        <strong>{formatMoney(invoice.totalAmount)} {invoice.currency.toUpperCase()}</strong>
                        <span>{new Date(invoice.createdAt).toLocaleString()}</span>
                        {invoice.status !== 'Paid' && (
                          <>
                            <Button text={invoiceActionLabel} onClick={() => void markInvoicePaid(invoice.id)} />
                            <span>{invoiceActionHint}</span>
                          </>
                        )}
                      </div>
                    </div>
                  )) : <p>Inga fakturor kopplade till vald order.</p>}
                </div>
              </TabsContent>
            </Tabs>
          </section>
        </div>
      </div>
    </div>
  );
};
